import React from 'react';
    import { motion } from 'framer-motion';
    import * as FiIcons from 'react-icons/fi';
    import SafeIcon from '../common/SafeIcon';
    import Navigation from '../components/Navigation';
    import CosmicBackground from '../components/CosmicBackground';

    const { FiTrendingUp, FiDollarSign, FiUsers, FiGift, FiArrowRight, FiBarChart3 } = FiIcons;

    const AffiliatePathwaysPage = () => {
      
      const benefits = [
        { icon: FiDollarSign, title: '20% Recurring Commissions', description: 'Earn a recurring 20% commission on all referrals for as long as they remain a customer.' },
        { icon: FiGift, title: '60-Day Cookie Window', description: 'Get credit for referrals up to 60 days after they click your link.' },
        { icon: FiBarChart3, title: 'Advanced Affiliate Dashboard', description: 'Track your clicks, referrals, and earnings in real-time with our powerful dashboard.' },
        { icon: FiUsers, title: 'Marketing Materials', description: 'Access a library of proven marketing materials to help you promote effectively.' },
      ];

      const howItWorks = [
        { step: '1', title: 'Sign Up', description: 'Join our affiliate program through our simple sign-up process.' },
        { step: '2', title: 'Share Your Link', description: 'Use your unique affiliate link and our marketing materials to spread the word.' },
        { step: '3', title: 'Earn Commissions', description: 'Receive a 20% recurring commission for every successful referral.' },
      ];

      return (
        <div className="min-h-screen bg-gray-900 text-white relative overflow-hidden">
          <CosmicBackground />
          <Navigation />

          {/* Hero Section */}
          <section className="pt-32 pb-20 relative z-10">
            <div className="container mx-auto px-6">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="text-center max-w-4xl mx-auto"
              >
                <div className="inline-flex items-center space-x-2 bg-green-900/30 px-4 py-2 rounded-full border border-green-500/30 mb-6">
                  <SafeIcon icon={FiTrendingUp} className="text-green-400" />
                  <span className="text-sm font-poppins text-green-200">Earn 20% Recurring</span>
                </div>
                <h1 className="text-5xl md:text-7xl font-orbitron font-bold mb-6 leading-tight">
                  <span className="clarity-gradient">Affiliate Pathways</span>
                </h1>
                <p className="text-xl md:text-2xl text-gray-300 mb-8 font-poppins font-light max-w-3xl mx-auto leading-relaxed">
                  Join our affiliate program and earn 20% recurring commissions by sharing the Clarity with Jahx ecosystem with your audience.
                </p>
                <motion.a href="#" target="_blank" rel="noopener noreferrer">
                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="bg-gradient-to-r from-green-600 to-emerald-600 px-8 py-4 rounded-full font-poppins font-semibold text-lg glow-button flex items-center space-x-2 mx-auto"
                    >
                        <span>Become an Affiliate</span>
                        <SafeIcon icon={FiArrowRight} />
                    </motion.button>
                </motion.a>
              </motion.div>
            </div>
          </section>

          {/* Benefits Section */}
          <section className="py-20 relative z-10">
            <div className="container mx-auto px-6">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="text-center mb-16"
              >
                <h2 className="text-4xl md:text-5xl font-orbitron font-bold mb-6">
                  Why Partner <span className="clarity-gradient">With Us?</span>
                </h2>
              </motion.div>
              <div className="grid md:grid-cols-2 gap-8 mb-16">
                {benefits.map((benefit, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    whileHover={{ y: -5 }}
                    className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 p-8 rounded-2xl border border-gray-700/50 backdrop-blur-sm hover:border-green-500/30 transition-all duration-300"
                  >
                    <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center mb-6">
                      <SafeIcon icon={benefit.icon} className="text-white text-2xl" />
                    </div>
                    <h3 className="text-2xl font-orbitron font-bold mb-4 text-white">{benefit.title}</h3>
                    <p className="text-gray-300 font-poppins leading-relaxed">{benefit.description}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* How It Works */}
          <section className="py-20 relative z-10">
            <div className="container mx-auto px-6">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="text-center mb-16"
              >
                <h2 className="text-4xl md:text-5xl font-orbitron font-bold mb-6">
                  Get Started in <span className="clarity-gradient">3 Easy Steps</span>
                </h2>
              </motion.div>
              <div className="grid md:grid-cols-3 gap-8 mb-16">
                {howItWorks.map((step, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="text-center"
                  >
                    <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6">
                      <span className="text-white font-orbitron font-bold text-xl">{step.step}</span>
                    </div>
                    <h3 className="text-xl font-orbitron font-bold mb-4 text-white">{step.title}</h3>
                    <p className="text-gray-300 font-poppins">{step.description}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-20 relative z-10">
            <div className="container mx-auto px-6">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="text-center bg-gradient-to-r from-green-900/40 to-emerald-900/40 p-12 rounded-3xl border border-green-500/30 backdrop-blur-sm max-w-4xl mx-auto"
              >
                <h2 className="text-4xl md:text-5xl font-orbitron font-bold mb-6">
                  Ready to Start <span className="clarity-gradient">Earning?</span>
                </h2>
                <p className="text-xl text-gray-300 font-poppins mb-8 max-w-2xl mx-auto">
                  Partner with a brand that's dedicated to helping soulful entrepreneurs succeed.
                </p>
                <motion.a href="#" target="_blank" rel="noopener noreferrer">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="bg-gradient-to-r from-green-600 to-emerald-600 px-12 py-4 rounded-full font-poppins font-semibold text-xl glow-button"
                  >
                    Join Our Affiliate Program
                  </motion.button>
                </motion.a>
              </motion.div>
            </div>
          </section>
        </div>
      );
    };

    export default AffiliatePathwaysPage;